import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { BrandSummary } from "@velivo/shared";
import { Field, SelectField } from "../components/Field";
import { EmptyState } from "../components/EmptyState";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";

/** FR-ADMIN-01 / FR-ADMIN-08 — create a Brand and choose its vector tenancy tier. */
export function CreateBrandPage() {
  const { user, refreshUser, setActiveBrand } = useAuth();
  const navigate = useNavigate();

  const organizations = user?.organizationGrants ?? [];
  const [organizationId, setOrganizationId] = useState(
    organizations[0]?.organizationId ?? "",
  );
  const [brandName, setBrandName] = useState("");
  const [vectorTenancyMode, setVectorTenancyMode] = useState<
    "pooled" | "dedicated"
  >("pooled");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  if (organizations.length === 0) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">Create a Brand</h1>
        </header>
        <div className="card">
          <EmptyState
            icon="lock"
            title="You can't create Brands"
            description="Only an Organization Admin can add a Brand. Ask yours to create it and grant you a role on it."
          />
        </div>
      </>
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const brand = await api<BrandSummary>(
        `/organizations/${organizationId}/brands`,
        { method: "POST", body: { brandName, vectorTenancyMode } },
      );
      await refreshUser();
      setActiveBrand(brand.brandId);
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        const fields = caught.fieldErrors();
        if (Object.keys(fields).length > 0) setFieldErrors(fields);
        else setError(caught.message);
      } else {
        setError("Could not reach the server. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Create a Brand</h1>
        <p className="page__subtitle">
          One Brand per company whose narrative you track. An agency creates one
          per client.
        </p>
      </header>

      <div className="card" style={{ maxWidth: 560 }}>
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}

          {organizations.length > 1 && (
            <SelectField
              label="Organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              {organizations.map((org) => (
                <option key={org.organizationId} value={org.organizationId}>
                  {org.organizationName}
                </option>
              ))}
            </SelectField>
          )}

          <Field
            label="Brand name"
            name="brandName"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            error={fieldErrors.brandName}
            required
          />

          <SelectField
            label="Ground Truth storage"
            value={vectorTenancyMode}
            onChange={(e) =>
              setVectorTenancyMode(e.target.value as "pooled" | "dedicated")
            }
            help={
              vectorTenancyMode === "pooled"
                ? "Shared, tenant-isolated storage. The right default for almost every Brand."
                : "A dedicated collection for this Brand alone. Reserved for contracts requiring stronger separation."
            }
          >
            <option value="pooled">Pooled (default)</option>
            <option value="dedicated">Dedicated</option>
          </SelectField>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
            }}
          >
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create Brand"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
